import type { EmailTemplate } from "@/queries/email-templates/email-templates.api";
import {
  useGetEmailTemplates,
  useGetEmailTemplateById,
} from "@/queries/email-templates/email-templates.api";
import { cn } from "@/utils/theme";
import { useEffect, useMemo, useState, useCallback } from "react";
import { Badge, Icon } from "@elaniintc/goes-design-system";
import { SearchDropdown } from "@/components/ui/search-dropdown";

interface TemplateSelectorProps {
  selectedTemplate?: string;
  onTemplateChange: (templateId: string) => void;
  className?: string;
  placeholder?: string;
  error?: string;
}

export const TemplateSelector = ({
  onTemplateChange,
  selectedTemplate,
  placeholder = "Seleccionar plantilla",
  className,
  error,
}: TemplateSelectorProps) => {
  // Estados para controlar la paginacion, la busqueda actual y la lista de plantillas acumuladas
  const [page, setPage] = useState(1);
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [allTemplates, setAllTemplates] = useState<EmailTemplate[]>([]);

  // Peticion para obtener las plantillas filtradas por estado activo y busqueda
  const {
    data: emailTemplatesData,
    isLoading,
    isFetching,
  } = useGetEmailTemplates({
    "filter[status]": "active",
    page,
    per_page: 20,
    ...(currentSearchQuery ? { "filter[search]": currentSearchQuery } : {}),
  });

  // Determinar si debemos buscar una plantilla seleccionada que no este en la lista actual
  // Esto es util cuando la plantilla guardada esta en una pagina mas adelante
  const shouldFetchSelectedTemplate =
    selectedTemplate && selectedTemplate !== "no-template";

  const { data: selectedTemplateResponse } = useGetEmailTemplateById(
    shouldFetchSelectedTemplate ? selectedTemplate : "",
  );

  // Opcion por defecto para cuando no se quiere usar ninguna plantilla
  const noTemplateOption: EmailTemplate = useMemo(
    () => ({
      id: "no-template",
      type: null,
      name: "Sin plantilla",
      description: "No usar una plantilla predefinida",
      subject: "",
      rich_text: "",
      variables: {},
      status: "active",
      created_at: "",
      updated_at: "",
      required_variables: [],
      optional_variables: [],
    }),
    [],
  );

  const handleSearch = useCallback((searchString: string) => {
    setCurrentSearchQuery(searchString);
    setPage(1);
    setAllTemplates([]);
  }, []);

  // Efecto para acumular las plantillas conforme se cargan mas paginas
  useEffect(() => {
    if (!emailTemplatesData?.data) return;

    if (currentSearchQuery) {
      setAllTemplates(emailTemplatesData.data);
    } else {
      setAllTemplates((prev) => {
        const existingIds = new Set(prev.map((t) => t.id));
        const newTemplates = emailTemplatesData.data.filter(
          (t) => !existingIds.has(t.id),
        );
        return [...prev, ...newTemplates];
      });
    }
  }, [emailTemplatesData, currentSearchQuery]);

  // Si la plantilla seleccionada se cargo por separado, se agrega a la lista principal
  useEffect(() => {
    if (!selectedTemplateResponse?.data) return;
    const selectedTemplateData = selectedTemplateResponse.data;

    setAllTemplates((prev) => {
      if (prev.some((t) => t.id === selectedTemplateData.id)) {
        return prev;
      }
      return [selectedTemplateData, ...prev];
    });
  }, [selectedTemplateResponse]);

  // valida si existe una pagina siguiente basandose en la respuesta de la API
  const hasNextPage = useMemo(() => {
    if (!emailTemplatesData?.meta) return false;
    const { current_page, last_page } = emailTemplatesData.meta;
    return current_page < last_page;
  }, [emailTemplatesData]);

  // cargar la siguiente pagina de resultados
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetching) {
      setPage((p) => p + 1);
    }
  }, [hasNextPage, isFetching]);

  // Combina la opcion "Sin plantilla" con el resto de las plantillas obtenidas
  const templatesWithNoTemplate = useMemo(() => {
    return [noTemplateOption, ...allTemplates];
  }, [noTemplateOption, allTemplates]);

  // Obtener los datos de la plantilla seleccionada para mostrarlos en el boton principal
  const selectedTemplateData = useMemo(() => {
    return templatesWithNoTemplate.find((t) => t.id === selectedTemplate);
  }, [templatesWithNoTemplate, selectedTemplate]);

  return (
    <div className="flex flex-col gap-1.5">
      <label className="font-semibold text-body-loud text-sm leading-2xl">
        Plantilla de notificación
        <sup className="text-danger ml-0.5">*</sup>
      </label>
      <SearchDropdown<EmailTemplate>
        widthClassName={cn("min-w-0 w-full flex rounded-lg", className)}
        contentWidthClassName="w-[var(--radix-dropdown-menu-trigger-width)]"
        searchPlaceholder="Buscar plantilla..."
        placeholder={placeholder}
        data={templatesWithNoTemplate}
        fetchNextPage={loadMore}
        onSearch={handleSearch}
        hasNextPage={hasNextPage}
        isLoading={isLoading}
        isEmpty={templatesWithNoTemplate.length === 1}
        isFetching={isFetching}
        selectedItem={selectedTemplateData}
        renderItem={(template, index, closeMenu, isSelectedShowing) => {
          const isSelected = template.id === selectedTemplate;

          if (index === -1) {
            return (
              <div className="min-w-0 flex flex-1 gap-3 overflow-hidden">
                <div className="flex items-center">
                  <Badge
                    shape="pill"
                    variant="action"
                    className="size-6 shrink-0 rounded-full flex items-center justify-center"
                  >
                    <Icon type="fa" name="copy" className="text-xs" />
                  </Badge>
                </div>
                <span className="flex-1 max-w-[90%] truncate text-left">
                  {template.name}
                </span>
              </div>
            );
          }

          return (
            <div
              className={cn(
                "rounded-lg cursor-pointer text-body relative flex items-center gap-2 py-2 px-3 text-sm outline-hidden select-none hover:bg-neutral-object-accent-silent focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50",
                isSelected && "bg-neutral-object-accent-silent",
              )}
              onClick={() => {
                if (isSelectedShowing) return;
                onTemplateChange(template.id);
                closeMenu();
              }}
            >
              <div className="w-full flex items-center gap-3">
                <div className="flex gap-2 items-center flex-1 min-w-0">
                  <Icon
                    type={isSelected ? "fas" : "far"}
                    name="copy"
                    className={cn(
                      "text-base shrink-0",
                      isSelected ? "text-action" : "text-body-quiet",
                    )}
                  />
                  <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                    <span className="text-body truncate font-semibold">
                      {template.name}
                    </span>
                    {template.description && template.id !== "no-template" && (
                      <span className="text-body-quiet text-xs truncate">
                        {template.description}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      />
      {error && (
        <div className="text-danger flex items-center gap-1.5">
          <span className="text-current text-xs leading-xl font-normal">
            {error}
          </span>
        </div>
      )}
    </div>
  );
};
